import { CarState, Input, newCar, speedOf } from './car';
import { datan2 } from './fmath';
import type { Track } from './track';
import { DT, STREAK_LAP_FRACTION, STREAK_MAX_BONUS_MPH, STREAK_THROTTLE_MIN } from './tuning';

/** Lap timing, checkpoints and the tuning streak, shared by every mode. */

export type LapEvent =
  | { type: 'lapStart' }
  | { type: 'checkpoint'; gate: number }
  | { type: 'lap'; ticks: number; valid: boolean; best: boolean }
  | { type: 'missedCheckpoint' };

export interface LapState {
  /** Tick the current lap started on, or -1 before crossing the line. */
  startTick: number;
  nextGate: number;
  valid: boolean;
  lastLapTicks: number | null;
  bestLapTicks: number | null;
  lapsDone: number;
  /** Last gate passed; respawn point after a crash. */
  lastGate: number;
  streakDist: number;
}

export function freshLap(): LapState {
  return { startTick: -1, nextGate: 0, valid: true, lastLapTicks: null, bestLapTicks: null, lapsDone: 0, lastGate: 0, streakDist: 0 };
}

/** Process gate crossings for a move (x0,y0)->(x1,y1) made during `tick`. */
export function updateLap(track: Track, lap: LapState, tick: number, x0: number, y0: number, x1: number, y1: number, out: LapEvent[]): void {
  const gates = track.gates;
  for (let i = 0; i < gates.length; i++) {
    if (!track.crossedGate(gates[i], x0, y0, x1, y1)) continue;
    if (i === 0) {
      if (lap.startTick >= 0 && lap.nextGate === 0) {
        const ticks = tick + 1 - lap.startTick;
        const valid = lap.valid;
        let best = false;
        if (valid && (lap.bestLapTicks === null || ticks < lap.bestLapTicks)) {
          lap.bestLapTicks = ticks;
          best = true;
        }
        lap.lastLapTicks = ticks;
        lap.lapsDone++;
        out.push({ type: 'lap', ticks, valid, best });
      } else if (lap.startTick >= 0 && lap.valid) {
        out.push({ type: 'missedCheckpoint' });
      }
      lap.startTick = tick + 1;
      lap.nextGate = gates.length > 1 ? 1 : 0;
      lap.valid = true;
      lap.lastGate = 0;
      out.push({ type: 'lapStart' });
    } else if (i === lap.nextGate && lap.startTick >= 0) {
      lap.nextGate = (i + 1) % gates.length;
      lap.lastGate = i;
      out.push({ type: 'checkpoint', gate: i });
    }
  }
}

/**
 * Tuning streak (design.md A7): full throttle, no contact, 75% of a lap
 * ⇒ +1 mph. Returns true when the bonus went up (caller refreshes perf).
 */
export function updateStreak(track: Track, car: CarState, lap: LapState, input: Input): boolean {
  if (car.crashed || car.contact || input.throttle < STREAK_THROTTLE_MIN * 127) {
    lap.streakDist = 0;
    return false;
  }
  lap.streakDist += speedOf(car) * DT;
  if (lap.streakDist >= STREAK_LAP_FRACTION * track.length) {
    lap.streakDist = 0;
    if (car.bonusMph < STREAK_MAX_BONUS_MPH) {
      car.bonusMph += 1;
      return true;
    }
  }
  return false;
}

/** Staggered two-wide grid behind the start line (design.md A3). */
export function gridCar(track: Track, slot: number): CarState {
  const g = track.gates[0];
  const back = 34 + slot * 26;
  const side = slot % 2 === 0 ? -14 : 14;
  const nx = -g.uy;
  const ny = g.ux;
  return newCar(g.cx - g.ux * back + nx * side, g.cy - g.uy * back + ny * side, datan2(g.uy, g.ux));
}

/** A fresh car just behind gate `gateIndex`, facing along the track. */
export function respawnCar(track: Track, gateIndex: number, bonusMph: number): CarState {
  const g = track.gates[gateIndex];
  const c = newCar(g.cx - g.ux * 24, g.cy - g.uy * 24, datan2(g.uy, g.ux));
  c.bonusMph = bonusMph;
  return c;
}
